import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import Chip from "@mui/material/Chip";
import Icon from "@mui/material/Icon";
import Tooltip from "@mui/material/Tooltip";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DataTable from "examples/Tables/DataTable";
import useApi from "components/Utils/api";
import toast from "react-hot-toast";
import ProviderConfigDialog from "../dialogs/ProviderConfigDialog";
import PermissionControl from "components/Utils/PermissionControl";

// The full set of providers a route can have — one row per type, no duplicates
const ALL_PROVIDER_TYPES = [
  { providerType: "IVERI",          paymentMethodType: "CARD",            label: "iVeri" },
  { providerType: "IVERI_ECOCASH",  paymentMethodType: "WALLET",          label: "iVeri (EcoCash)" },
  // { providerType: "SWITCH",         paymentMethodType: "CARD",            label: "Switch (Card)" },
  { providerType: "ECOCASH",        paymentMethodType: "WALLET",          label: "EcoCash" },
  { providerType: "ONEMONEY",       paymentMethodType: "WALLET",          label: "OneMoney" },
  { providerType: "CBZ_SETTLEMENT", paymentMethodType: "BANK_SETTLEMENT", label: "CBZ Settlement" },
];

const configStatusColor = { COMPLETE: "success", INCOMPLETE: "warning", INVALID: "error" };

// ── Cell renderers ───────────────────────────────────────────────────────────

function ConfigStatusCell({ cell }) {
  if (!cell.value) {
    return <Chip label="Not configured" size="small" color="default" />;
  }
  return <Chip label={cell.value} size="small" color={configStatusColor[cell.value] || "default"} />;
}
ConfigStatusCell.propTypes = { cell: PropTypes.shape({ value: PropTypes.string }) };

function EnabledCell({ cell }) {
  if (cell.value === undefined || cell.value === null) return null;
  return <Chip label={cell.value ? "Enabled" : "Disabled"} size="small" color={cell.value ? "success" : "default"} />;
}
EnabledCell.propTypes = { cell: PropTypes.shape({ value: PropTypes.bool }) };

function CurrenciesCell({ cell }) {
  if (!cell.value) return <MDTypography variant="caption" color="text">All</MDTypography>;
  return <MDTypography variant="caption">{cell.value}</MDTypography>;
}
CurrenciesCell.propTypes = { cell: PropTypes.shape({ value: PropTypes.string }) };

function ProviderActionsCell({ row, onConfigure, onToggle }) {
  const item = row.original;
  const hasConfig = !!item.id;
  return (
    <MDBox display="flex" gap={1} alignItems="center">
      <PermissionControl permission={{ module: "SettlementRoutes", subModule: "Update" }}>
        <Tooltip title={hasConfig ? "Edit Config" : "Configure"} arrow>
          <MDButton size="small" variant="gradient" color={hasConfig ? "info" : "warning"} onClick={() => onConfigure(item)}>
            <Icon sx={{ mr: 0.5 }}>{hasConfig ? "edit" : "settings"}</Icon>
            {hasConfig ? "Edit" : "Configure"}
          </MDButton>
        </Tooltip>
      </PermissionControl>
      {hasConfig && (
        <PermissionControl permission={{ module: "SettlementRoutes", subModule: "Update" }}>
          <Tooltip title={item.enabled ? "Disable" : "Enable"} arrow>
            <MDButton size="small" variant="outlined" color={item.enabled ? "error" : "success"} iconOnly onClick={() => onToggle(item)}>
              <Icon>{item.enabled ? "toggle_off" : "toggle_on"}</Icon>
            </MDButton>
          </Tooltip>
        </PermissionControl>
      )}
    </MDBox>
  );
}
ProviderActionsCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      id: PropTypes.string,
      enabled: PropTypes.bool,
      providerType: PropTypes.string,
    }),
  }),
  onConfigure: PropTypes.func.isRequired,
  onToggle: PropTypes.func.isRequired,
};

// ── Tab ──────────────────────────────────────────────────────────────────────

ProviderConfigsTab.propTypes = {
  routeId: PropTypes.string.isRequired,
};

export default function ProviderConfigsTab({ routeId }) {
  const api = useApi();
  const [configMap, setConfigMap] = useState({});   // providerType → config object
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogInitial, setDialogInitial] = useState(null);

  const load = () => {
    api.get(`settlement-routes/${routeId}/provider-configs`)
      .then((res) => {
        const map = {};
        (res.data || []).forEach((c) => { map[c.providerType] = c; });
        setConfigMap(map);
      })
      .catch(() => toast.error("Failed to load provider configs"));
  };

  useEffect(() => { load(); }, [routeId]);

  const handleConfigure = (row) => {
    // Pre-fill dialog with existing config if present, otherwise just the provider type
    const existing = configMap[row.providerType];
    setDialogInitial(existing
      ? { ...existing }
      : { providerType: row.providerType, paymentMethodType: row.paymentMethodType }
    );
    setDialogOpen(true);
  };

  const handleToggleEnabled = async (item) => {
    try {
      await api.put(`settlement-routes/${routeId}/provider-configs/${item.id}/status?enabled=${!item.enabled}`);
      toast.success(`Provider ${item.enabled ? "disabled" : "enabled"}`);
      load();
    } catch (error) {
      toast.error(error?.response?.data?.error?.message || "Failed to toggle provider status");
    }
  };

  const handleSave = async (data) => {
    try {
      // Always use the upsert endpoint (POST) — the backend handles insert-or-update
      await api.post(`settlement-routes/${routeId}/provider-configs`, data);
      toast.success("Provider config saved");
      setDialogOpen(false);
      setDialogInitial(null);
      load();
    } catch (error) {
      toast.error(error?.response?.data?.error?.message || "Failed to save provider config");
    }
  };

  // Build rows by merging ALL_PROVIDER_TYPES with whatever is already configured
  const rows = ALL_PROVIDER_TYPES.map((pt) => {
    const cfg = configMap[pt.providerType];
    return {
      providerType:      pt.providerType,
      label:             pt.label,
      paymentMethodType: cfg?.paymentMethodType || pt.paymentMethodType,
      configStatus:      cfg?.configStatus || null,
      enabled:           cfg?.enabled ?? null,
      supportedCurrencies: cfg?.supportedCurrencies || null,
      id:                cfg?.id || null,
    };
  });

  const columns = [
    { Header: "Provider",      accessor: "label",              width: "22%", align: "left" },
    { Header: "Method",        accessor: "paymentMethodType",  width: "15%", align: "left" },
    { Header: "Status",        accessor: "configStatus",       width: "18%", align: "left",   Cell: ConfigStatusCell },
    { Header: "Enabled",       accessor: "enabled",            width: "12%", align: "center", Cell: EnabledCell },
    { Header: "Currencies",    accessor: "supportedCurrencies",width: "13%", align: "left",   Cell: CurrenciesCell },
    {
      Header: "Actions",
      accessor: "actions",
      width: "20%",
      align: "left",
      // eslint-disable-next-line react/prop-types
      Cell: ({ row }) => (
        <ProviderActionsCell row={row} onConfigure={handleConfigure} onToggle={handleToggleEnabled} />
      ),
    },
  ];

  return (
    <MDBox>
      <MDBox mb={2}>
        <MDTypography variant="h6">Provider Configurations</MDTypography>
        <MDTypography variant="caption" color="text">
          Each route can have one configuration per provider type. Click Configure to set credentials.
        </MDTypography>
      </MDBox>
      <DataTable table={{ columns, rows }} showTotalEntries={false} isSorted={false} noEndBorder entriesPerPage={false} />
      <ProviderConfigDialog
        open={dialogOpen}
        onClose={() => { setDialogOpen(false); setDialogInitial(null); }}
        onSave={handleSave}
        initial={dialogInitial}
      />
    </MDBox>
  );
}
